import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Typography, Autocomplete,
    // Checkbox, ListItemText — used by the hidden pick-list fields below.
} from '@mui/material';
// import MenuItem from '@material-ui/core/MenuItem'; // used by the hidden pick-list fields below.
import { PhoneInput } from 'react-international-phone';
// The literal build/ path — see CreateClientDialog.comp.jsx.
import 'react-international-phone/build/index.css';
import { getCountries, getPhoneCountries, getStatesByCountry, useGeo } from '../../../utils/geo';
import EntryModeToggle from './EntryModeToggle.comp';
import { isValidEmail, getPhoneDialCode, splitPhoneByDialCode } from '../../../utils/utils';
// Pick-list constants for the hidden fields below — restore with them.
// import {
//     PRIMARY_ACTIVITY_CODE, PRIMARY_ACTIVITY_CODE_VERBOSE,
//     ADDITIONAL_CAPABILITY_CODE, ADDITIONAL_CAPABILITY_CODE_VERBOSE,
//     ORGANIZATION_TYPE_CODE, ORGANIZATION_TYPE_CODE_VERBOSE,
// } from '../../../utils/constants';

const ORANGE = 'var(--brand-primary, #FF6900)';

const blank = {
    org_name: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_title: '',
    contact_email: '',
    org_address: '',
    org_city: '',
    postal_code: '',
    org_country: '',
    // OrgInstitute fields — "Schema for Data Collection" §3. The same set the
    // single-school form and each network school card collect in
    // CreateClientDialog, so a school reads the same however it was added.
    number_of_learners: '',
    institute_phone_country_code: '',
    institute_phone_number: '',
    institute_type: '',
    institute_primary_activities: [],
    institute_address_line1: '',
    institute_address_line2: '',
    institute_state: '',
    institute_website: '',
    institute_additional_capabilities: [],
};

/** Pure, so it drives both the inline messages and the button state. */
const validate = (fields) => {
    const e = {};
    if (!fields.org_name) e.org_name = 'Required';
    if (!fields.contact_first_name) e.contact_first_name = 'Required';
    if (!fields.contact_last_name) e.contact_last_name = 'Required';
    if (!fields.contact_email) e.contact_email = 'Required';
    else if (!isValidEmail(fields.contact_email)) e.contact_email = 'Invalid email address';
    // Same as CreateClientDialog's single school: the combined address field
    // is hidden, so Line 1 carries the requirement.
    if (!fields.institute_address_line1) e.institute_address_line1 = 'Required';
    if (!fields.org_city) e.org_city = 'Required';
    if (!fields.postal_code) e.postal_code = 'Required';
    if (!fields.org_country) e.org_country = 'Required';
    return e;
};

/**
 * Add ONE school to a network that already exists.
 *
 * PW deck slide 42. This is the only way a school joins an existing network —
 * the note on that slide is explicit: "Schools under an existing network can
 * only be added from here". It is deliberately NOT a dropdown on the ordinary
 * Add-a-School form.
 *
 * TWO DECKS SPECIFY THIS ONE DIALOG, with different copy, because two
 * different people reach it through the same route (/dq_pro/clients/list):
 *
 *   Partner Workspace deck, slide 42 — a PARTNER adding a school to one of
 *     the networks it manages. Addressed as "Add a school to <network name>",
 *     because the partner manages several and needs to know which.
 *
 *   Network Workspace deck, slide 21 — a NETWORK adding a school to ITSELF.
 *     Addressed as "your network", because there is only one it could mean.
 *
 * So the copy switches on who is looking, not on which row was clicked.
 *
 * NOTE — the two decks disagree on licences. Slide 42 says the school
 * "inherits all three"; slide 21 says it "starts with no licenses". The code
 * agrees with slide 21: createClient sets licenses_dq_assess.total and .used
 * to 0, which is why every school row reads "0 used / 0 allocated / 0 total"
 * until seats are assigned. Both texts are reproduced as their decks specify;
 * the contradiction is flagged for DQ rather than silently resolved here.
 *
 * @param {object} network         the network row — { org_code, org_name }
 * @param {boolean} viewerIsNetwork true when a network is adding to itself
 *                                  (slide 21), false for a partner (slide 42)
 */

const AddSchoolToNetworkDialog = ({ open, onClose, onSubmit, loading, network, viewerIsNetwork = false, onUpload, uploadLabel, manualLabel }) => {
    useGeo(); // re-render with the API's lists once they load
    const [fields, setFields] = useState(blank);
    const [touched, setTouched] = useState({});
    const [submitAttempted, setSubmitAttempted] = useState(false);

    useEffect(() => {
        if (open) {
            setFields(blank);
            setTouched({});
            setSubmitAttempted(false);
        }
    }, [open]);

    // Validated live; messages appear once a field is visited or Add is pressed.
    const liveErrors = validate(fields);
    const isFormValid = Object.keys(liveErrors).length === 0;
    const errors = Object.fromEntries(
        Object.entries(liveErrors).filter(([k]) => submitAttempted || touched[k]),
    );

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFields((f) => ({ ...f, [name]: value }));
        setTouched((t) => ({ ...t, [name]: true }));
    };

    const handleAdd = () => {
        setSubmitAttempted(true);
        if (!isFormValid) return;

        onSubmit({
            ...fields,
            is_school_network: false,
            // Names the network this school joins. The API consumes it to build
            // the school's org_code — that code is what records the relationship,
            // so nothing is stored under this name.
            network_org_code: network?.org_code,
            contact_name: `${fields.contact_first_name} ${fields.contact_last_name}`.trim(),
            number_of_learners: fields.number_of_learners === '' ? null : Number(fields.number_of_learners),
            // Stored a second time on the institute record — same mapping as
            // CreateClientDialog's single school, where Line 1 is the real address.
            institute_head_title: fields.contact_title,
            institute_address: fields.institute_address_line1,
        });
    };

    // Used by the hidden pick-list fields below — restore with them.
    // const multiSelectProps = (verbose) => ({
    //     multiple: true,
    //     renderValue: (selected) => selected.map((code) => verbose[code]).join(', '),
    // });
    const statesForCountry = getStatesByCountry(fields.org_country);

    return (
        // A click outside the form does not close it — what was typed stays (LA-PW-05Oct-2).
        <Dialog open={open} onClose={(event, reason) => { if (reason !== 'backdropClick') onClose(); }} maxWidth="sm" fullWidth
                PaperProps={{ sx: { border: '2px solid #000000', borderRadius: '15px' } }}>
            <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center', pb: 0.5 }}>
                {/* "Add a School" on both halves of the network's own Add (Ian-SNW-07Oct2026-1, slides 48-49). */}
                {viewerIsNetwork ? 'Add a School' : `Add a school to ${network?.org_name || 'this network'}`}
            </DialogTitle>
            <DialogContent>
                {/* The switch first, then the one line both workspaces now share
                    (Ian-PW-11Oct-01, and slide 48 for the network). */}
                {onUpload && <EntryModeToggle centered active="manual" manualLabel={manualLabel} uploadLabel={uploadLabel} onUpload={onUpload} />}
                <Typography sx={{ textAlign: 'center', fontSize: 15, mb: 2, color: 'var(--brand-text-muted, #6B7280)' }}>
                    Register a school and its main contact.
                </Typography>

                <TextField
                    label="School Name" name="org_name"
                    value={fields.org_name} onChange={handleChange}
                    error={!!errors.org_name} helperText={errors.org_name}
                    fullWidth sx={{ mb: 2 }} autoFocus
                />

                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-text-muted, #6B7280)', mb: 1 }}>
                    School contact
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                        label="First Name" name="contact_first_name"
                        value={fields.contact_first_name} onChange={handleChange}
                        error={!!errors.contact_first_name} helperText={errors.contact_first_name}
                        fullWidth
                    />
                    <TextField
                        label="Last Name" name="contact_last_name"
                        value={fields.contact_last_name} onChange={handleChange}
                        error={!!errors.contact_last_name} helperText={errors.contact_last_name}
                        fullWidth
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                        label="Title / Role (optional)" name="contact_title"
                        value={fields.contact_title} onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        label="Contact Email" name="contact_email" type="email"
                        value={fields.contact_email} onChange={handleChange}
                        error={!!errors.contact_email} helperText={errors.contact_email}
                        fullWidth
                    />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <PhoneInput
                        countries={getPhoneCountries()}
                        defaultCountry="us"
                        placeholder="School Phone Number"
                        disableDialCodePrefill
                        // Uncontrolled and lifted above the dialog — see CreateClientDialog.
                        countrySelectorStyleProps={{ dropdownStyleProps: { style: { zIndex: 1500 } } }}
                        onChange={(phone, countryIso2) => {
                            if (!phone) {
                                setFields((f) => ({ ...f, institute_phone_country_code: '', institute_phone_number: '' }));
                                return;
                            }
                            const dialCode = getPhoneDialCode(countryIso2);
                            setFields((f) => ({
                                ...f,
                                institute_phone_country_code: dialCode,
                                institute_phone_number: splitPhoneByDialCode(phone, dialCode),
                            }));
                        }}
                        inputProps={{ name: 'institute_phone_number', autoComplete: 'tel' }}
                        inputStyle={{ flex: 1, minWidth: 0 }}
                        style={{
                            width: '100%',
                            '--react-international-phone-height': '56px',
                            '--react-international-phone-font-size': '16px',
                            '--react-international-phone-border-color': 'rgba(0, 0, 0, 0.23)',
                        }}
                    />
                </Box>
                {/*
                  * Number of Learners, School Organization Type, School Primary
                  * Role / Activity and Additional Capabilities are hidden per
                  * request — same as the single-school form and the network's
                  * school cards in CreateClientDialog. Fields kept in
                  * state/payload, just not shown.
                  */}
                {/*
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                        label="Number of Learners" name="number_of_learners" type="number"
                        value={fields.number_of_learners} onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        select label="School Organization Type" name="institute_type"
                        value={fields.institute_type} onChange={handleChange}
                        fullWidth
                    >
                        {Object.values(ORGANIZATION_TYPE_CODE).map((code) => (
                            <MenuItem key={code} value={code}>{ORGANIZATION_TYPE_CODE_VERBOSE[code]}</MenuItem>
                        ))}
                    </TextField>
                </Box>
                <TextField
                    select label="School Primary Role / Activity" name="institute_primary_activities"
                    value={fields.institute_primary_activities} onChange={handleChange}
                    fullWidth sx={{ mb: 2 }}
                    SelectProps={multiSelectProps(PRIMARY_ACTIVITY_CODE_VERBOSE)}
                >
                    {Object.values(PRIMARY_ACTIVITY_CODE).map((code) => (
                        <MenuItem key={code} value={code}>
                            <Checkbox checked={fields.institute_primary_activities.indexOf(code) > -1} />
                            <ListItemText primary={PRIMARY_ACTIVITY_CODE_VERBOSE[code]} />
                        </MenuItem>
                    ))}
                </TextField>
                */}
                <TextField
                    label="School Website (optional)" name="institute_website"
                    value={fields.institute_website} onChange={handleChange}
                    fullWidth sx={{ mb: 2 }} autoComplete="url"
                />
                {/* Additional Capabilities hidden per request — see above. */}
                {/*
                <TextField
                    select label="Additional Capabilities" name="institute_additional_capabilities"
                    value={fields.institute_additional_capabilities} onChange={handleChange}
                    fullWidth sx={{ mb: 2 }}
                    SelectProps={multiSelectProps(ADDITIONAL_CAPABILITY_CODE_VERBOSE)}
                >
                    {Object.values(ADDITIONAL_CAPABILITY_CODE).map((code) => (
                        <MenuItem key={code} value={code}>
                            <Checkbox checked={fields.institute_additional_capabilities.indexOf(code) > -1} />
                            <ListItemText primary={ADDITIONAL_CAPABILITY_CODE_VERBOSE[code]} />
                        </MenuItem>
                    ))}
                </TextField>
                */}

                {/* The combined "School Address" field is hidden, as on the
                    single-school form — Line 1 is the required address. */}
                <TextField
                    label="School Address — Line 1" name="institute_address_line1"
                    value={fields.institute_address_line1} onChange={handleChange}
                    error={!!errors.institute_address_line1} helperText={errors.institute_address_line1}
                    fullWidth sx={{ mb: 2 }}
                />
                <TextField
                    label="School Address — Line 2 (optional)" name="institute_address_line2"
                    value={fields.institute_address_line2} onChange={handleChange}
                    fullWidth sx={{ mb: 2 }}
                />
                {/* Searchable — a plain select can't be typed into. */}
                <Autocomplete
                    options={getCountries()}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                    value={getCountries().find((c) => c.code === fields.org_country) || null}
                    onChange={(event, newValue) => {
                        // The state list is keyed by country — clear a stale state.
                        setFields((f) => ({ ...f, org_country: newValue?.code || '', institute_state: '' }));
                        setTouched((t) => ({ ...t, org_country: true }));
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Country"
                            variant="outlined"
                            error={!!errors.org_country}
                            helperText={errors.org_country}
                        />
                    )}
                    fullWidth
                    sx={{ mb: 2 }}
                />
                <Autocomplete
                    options={statesForCountry}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                    value={statesForCountry.find((st) => st.code === fields.institute_state) || null}
                    onChange={(event, newValue) => {
                        setFields((f) => ({ ...f, institute_state: newValue ? newValue.code : '' }));
                    }}
                    disabled={!fields.org_country || statesForCountry.length === 0}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="School State / Province / Region"
                            variant="outlined"
                            placeholder={
                                !fields.org_country
                                    ? 'Select country first'
                                    : statesForCountry.length === 0 ? 'N/A' : 'Select state'
                            }
                        />
                    )}
                    fullWidth
                    sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                        label="City" name="org_city"
                        value={fields.org_city} onChange={handleChange}
                        error={!!errors.org_city} helperText={errors.org_city}
                        fullWidth
                    />
                    <TextField
                        label="Postal Code" name="postal_code"
                        value={fields.postal_code} onChange={handleChange}
                        error={!!errors.postal_code} helperText={errors.postal_code}
                        fullWidth
                    />
                </Box>

                {/* Each deck's own notice — see the licences caveat in the header. */}
                {/* No notice box in either workspace ("Remove this box"). */}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: 'var(--brand-text-muted, #6B7280)' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleAdd}
                    disabled={loading || !isFormValid}
                    variant="contained"
                    sx={{
                        textTransform: 'none', fontWeight: 700, borderRadius: '50px', px: 4,
                        backgroundColor: ORANGE, '&:hover': { backgroundColor: '#c9541e' },
                        '&.Mui-disabled': {
                            backgroundColor: 'var(--brand-border, #E5E7EB)',
                            color: 'var(--brand-text-muted, #9CA3AF)',
                        },
                    }}
                >
                    {loading ? 'Adding...' : 'Add school'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddSchoolToNetworkDialog;
